import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useCapabilityDetail } from '@/lib/capability-detail-context';
import { WORKING_COPY_ID } from '@/components/capability-detail/CapabilityDetailLayout';
import type { CapabilityRevision } from '@/types';
import { toast } from 'sonner';

export interface UseCapabilityActionsOptions {
    appKey: string;
    name: string;
    typeLabel: string;
    /** The entity data (must contain working_revision, immutable_revisions, status). */
    data: Record<string, unknown> | null;
    /** Called after mutations to refresh data. */
    refetch: () => Promise<unknown>;
    /** Override internal save for agent/model/workflow custom save logic. */
    customSave?: () => void | Promise<void>;
    /** External dirty flag — accompanies customSave. */
    customIsDirty?: boolean;
    /** External saving flag — accompanies customSave. */
    customIsSaving?: boolean;
    /** Called after publish succeeds (e.g. refetchWithoutCache for bundle-based pages). */
    onAfterPublish?: () => void | Promise<void>;
}

interface EntityShape {
    working_revision?: { id?: string };
    immutable_revisions?: CapabilityRevision[];
    status?: string;
}

function computeNextVersion(immutableRevisions: CapabilityRevision[]): { major: number; minor: number; patch: number } {
    let major = 0;
    let minor = 0;
    let patch = 1;
    if (immutableRevisions.length > 0) {
        let maxNumeric = -1;
        for (const rev of immutableRevisions) {
            const m = rev.major ?? 0;
            const n = rev.minor ?? 0;
            const p = rev.patch ?? 0;
            const numeric = m * 1_000_000 + n * 1_000 + p;
            if (numeric > maxNumeric) {
                maxNumeric = numeric;
                major = m;
                minor = n;
                patch = p;
            }
        }
        patch += 1;
    }
    return { major, minor, patch };
}

export function useCapabilityActions({
    appKey,
    name,
    typeLabel,
    data,
    refetch,
    customSave,
    customIsDirty,
    customIsSaving,
    onAfterPublish,
}: UseCapabilityActionsOptions) {
    const queryClient = useQueryClient();
    const detailShell = useCapabilityDetail();
    const [isPublishing, setIsPublishing] = useState(false);
    const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
    const [pendingEditRevisionId, setPendingEditRevisionId] = useState<string | null>(null);

    const overviewDeferredSave = detailShell?.overviewDeferredSave ?? null;
    const isViewingPublished = detailShell?.isViewingPublished ?? false;
    const selectedRevisionId = detailShell?.selectedRevisionId ?? WORKING_COPY_ID;

    const entity = data as EntityShape | null;
    const hasWorkingRevision = entity?.working_revision != null;
    const isArchived = (entity?.status ?? '').toLowerCase() === 'archived';
    const immutableRevisions: CapabilityRevision[] = (entity?.immutable_revisions ?? []) as CapabilityRevision[];

    const selectedPublishedLabel = (() => {
        if (selectedRevisionId === WORKING_COPY_ID) return null;
        const rev = immutableRevisions.find((r) => r.id === selectedRevisionId);
        return rev ? `v${rev.version_string}` : null;
    })();

    const isDirty = customIsDirty ?? overviewDeferredSave?.isDirty ?? false;
    const isSaving = customIsSaving ?? overviewDeferredSave?.isSaving ?? false;

    const save = useCallback(async () => {
        if (customSave) {
            await customSave();
        } else if (overviewDeferredSave?.isDirty) {
            await overviewDeferredSave.save();
        }
    }, [customSave, overviewDeferredSave]);

    const publish = useCallback(async () => {
        if (!appKey || !name || !data) return;
        setIsPublishing(true);
        try {
            if (isDirty) {
                if (customSave) {
                    await customSave();
                } else if (overviewDeferredSave?.isDirty) {
                    await overviewDeferredSave.save();
                }
            }

            const revId = (data as EntityShape).working_revision?.id;
            if (!revId) {
                toast.error('No working revision found', {
                    description: 'Cannot publish without a working revision.',
                });
                return;
            }

            const { major, minor, patch } = computeNextVersion(immutableRevisions);
            const encodedName = encodeURIComponent(name);
            await api.post(
                `/applications/${appKey}/capabilities/${encodedName}/revisions/${revId}/publish`,
                { major, minor, patch },
            );
            await refetch();
            if (onAfterPublish) await onAfterPublish();
            queryClient.invalidateQueries({ queryKey: ['capability', appKey, name] });
            queryClient.invalidateQueries({ queryKey: ['revisions', appKey, name] });
            toast.success(`${typeLabel} published`, {
                description: `Published as v${major}.${minor}.${patch}`,
            });
        } catch (err: unknown) {
            const message =
                (err as { response?: { data?: { error?: string } } })?.response?.data?.error
                ?? 'An unexpected error occurred. Please try again.';
            toast.error(`Failed to publish ${typeLabel.toLowerCase()}`, {
                description: message,
            });
        } finally {
            setIsPublishing(false);
        }
    }, [appKey, name, data, isDirty, customSave, overviewDeferredSave, immutableRevisions, typeLabel, refetch, onAfterPublish, queryClient]);

    const executeEdit = useCallback(async (params: { revisionId?: string; force?: boolean }) => {
        if (!appKey || !name) return;
        try {
            const encodedName = encodeURIComponent(name);
            await api.post(`/applications/${appKey}/capabilities/${encodedName}/edit`, {
                revision_id: params.revisionId ?? '',
                force: params.force ?? false,
            });
            await refetch();
            queryClient.invalidateQueries({ queryKey: ['capability', appKey, name] });
            detailShell?.bumpWorkingCopyVersion();
            toast.success('Working copy created', {
                description: `You can now edit this ${typeLabel.toLowerCase()}.`,
            });
        } catch {
            toast.error('Failed to create working copy');
        }
    }, [appKey, name, typeLabel, refetch, queryClient, detailShell]);

    const edit = useCallback(() => {
        const revId = selectedRevisionId !== WORKING_COPY_ID ? selectedRevisionId : undefined;
        if (hasWorkingRevision && revId) {
            setPendingEditRevisionId(revId);
            setReplaceDialogOpen(true);
        } else {
            void executeEdit({ revisionId: revId });
        }
    }, [selectedRevisionId, hasWorkingRevision, executeEdit]);

    const handleConfirmReplace = useCallback(() => {
        setReplaceDialogOpen(false);
        if (pendingEditRevisionId) {
            void executeEdit({ revisionId: pendingEditRevisionId, force: true });
        }
        setPendingEditRevisionId(null);
    }, [pendingEditRevisionId, executeEdit]);

    const openArchiveDialog = useCallback(() => {
        detailShell?.openArchiveDialog();
    }, [detailShell]);

    const openSettings = useCallback(() => {
        detailShell?.setIsSettingsOpen(true);
    }, [detailShell]);

    return {
        save,
        publish,
        edit,
        openArchiveDialog,
        openSettings,
        isDirty,
        isSaving,
        isPublishing,
        hasWorkingRevision,
        isViewingPublished,
        isArchived,
        selectedPublishedLabel,
        replaceDialogOpen,
        setReplaceDialogOpen,
        handleConfirmReplace,
    };
}
